import { useState, useEffect } from "react"
import Select from 'react-select'
import Modal from "../../../shared/modal/Modal"
import Button from "../../../shared/components/Button/Button"
import Input from "../../../shared/components/form/input"
import RequiredMark from "../../../shared/components/form/RequiredMark"
import CepSearchTrigger from "../../../shared/components/CepSearchTrigger/CepSearchTrigger"
import FormLayout from "../../../shared/components/FormLayout/FormLayout"
import SelectStyles from "../../../shared/Select/SelectStyle"
import useFlashMessage from "../../../hooks/useFlashMessage"
import { maskCEP, normalizeCEPDigits } from "../../../utils/formatters"
import { formatLocation, EMPTY_LOCATION } from "../../../utils/generatorLocation"
import "./GeneratorLocationModal.css"

const BRAZIL_STATES = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO']
    .map((uf) => ({ label: uf, value: uf }))

const REQUIRED = [
    ['logradouro', 'o logradouro'],
    ['number', 'o número'],
    ['district', 'o bairro'],
    ['city', 'a cidade'],
    ['state', 'a UF'],
    ['zipCode', 'o CEP'],
]

// Pergunta onde o gerador está: no mesmo endereço do cliente ou em outro. Usado ao vincular o gerador
// ao cliente e ao editar o endereço de um gerador já vinculado.
// Fechar no X (sem confirmar) mantém o que já estava — num gerador novo, "mesmo do cliente".
function GeneratorLocationModal({ modalStatus, generator, client, initial, onConfirm, onClose }) {
    const { setFlashMessage } = useFlashMessage()
    const [useClientAddress, setUseClientAddress] = useState(true)
    const [fields, setFields] = useState(EMPTY_LOCATION)

    useEffect(() => {
        if (!modalStatus) return
        const different = initial && initial.sameAsClient === false
        setUseClientAddress(!different)
        setFields(different ? { ...EMPTY_LOCATION, ...initial } : EMPTY_LOCATION)
    }, [modalStatus, initial])

    function handleChange(e) {
        const { name, value } = e.target
        setFields((prev) => ({ ...prev, [name]: name === 'zipCode' ? normalizeCEPDigits(value) : value }))
    }

    function handleCepFound(address) {
        setFields((prev) => ({
            ...prev,
            logradouro: address.street || prev.logradouro,
            district: address.district || prev.district,
            city: address.city || prev.city,
            state: address.state || prev.state,
            cityCode: address.cityCode || prev.cityCode,
        }))
    }

    function confirm(e) {
        e.preventDefault()
        // o Modal é portal, mas o submit borbulha pela árvore React e acionaria o form de baixo
        e.stopPropagation()

        if (useClientAddress) {
            onConfirm({ sameAsClient: true })
            return
        }
        const missing = REQUIRED.find(([field]) => !String(fields[field] || '').trim())
        if (missing) {
            setFlashMessage(`Informe ${missing[1]} do endereço do gerador`, 'error')
            return
        }
        onConfirm({ ...fields, sameAsClient: false })
    }

    return (
        <Modal
            modalStatus={modalStatus}
            changeStatus={() => onClose()}
            w={60}
            content={
                <FormLayout
                    title="Endereço do gerador"
                    requiredLegend
                    onSubmit={confirm}
                    actions={
                        <>
                            <Button variant="save" type="submit">Confirmar</Button>
                            <Button variant="cancel" type="button" onClick={() => onClose()}>Cancelar</Button>
                        </>
                    }
                >
                    <div className="fl-row">
                        <div className="fl-col-wide form-group">
                            <p className="gl-question">
                                Deseja usar o mesmo endereço do cliente para abrir a OS do gerador <strong>{generator?.name}</strong>?
                            </p>
                        </div>
                    </div>

                    <div className="gl-options">
                        <label className={`gl-option ${useClientAddress ? 'gl-option-active' : ''}`}>
                            <input type="radio" name="gl-mode" checked={useClientAddress} onChange={() => setUseClientAddress(true)} />
                            <span>
                                <span className="gl-option-title">Usar o mesmo endereço do cliente</span>
                                <span className="gl-option-text">{formatLocation(client) || 'Cliente sem endereço cadastrado'}</span>
                            </span>
                        </label>
                        <label className={`gl-option ${!useClientAddress ? 'gl-option-active' : ''}`}>
                            <input type="radio" name="gl-mode" checked={!useClientAddress} onChange={() => setUseClientAddress(false)} />
                            <span>
                                <span className="gl-option-title">Usar um endereço diferente</span>
                                <span className="gl-option-text">Onde o gerador realmente está instalado</span>
                            </span>
                        </label>
                    </div>

                    {!useClientAddress && (
                        <>
                            <div className="fl-row">
                                <div className="fl-col-narrow form-group">
                                    <CepSearchTrigger required text="CEP" name="zipCode" placeholder="00000-000" handleOnChange={handleChange} value={maskCEP(fields.zipCode)} onFound={handleCepFound} />
                                </div>
                                <div className="fl-col-wide form-group">
                                    <Input required text="Logradouro" type="text" name="logradouro" handleOnChange={handleChange} value={fields.logradouro || ''} />
                                </div>
                                <div className="fl-col-narrow form-group">
                                    <Input required text="Número" type="text" name="number" handleOnChange={handleChange} value={fields.number || ''} />
                                </div>
                            </div>
                            <div className="fl-row">
                                <div className="fl-col-mid form-group">
                                    <Input text="Complemento" type="text" name="complement" handleOnChange={handleChange} value={fields.complement || ''} />
                                </div>
                                <div className="fl-col-mid form-group">
                                    <Input required text="Bairro" type="text" name="district" handleOnChange={handleChange} value={fields.district || ''} />
                                </div>
                                <div className="fl-col-mid form-group">
                                    <Input required text="Cidade" type="text" name="city" handleOnChange={handleChange} value={fields.city || ''} />
                                </div>
                                <div className="fl-col-narrow form-group">
                                    <label className="input-label">UF:<RequiredMark /></label>
                                    <Select
                                        options={BRAZIL_STATES}
                                        onChange={(opt) => setFields((prev) => ({ ...prev, state: opt.value }))}
                                        styles={SelectStyles}
                                        menuPortalTarget={document.body}
                                        value={BRAZIL_STATES.find((s) => s.value === fields.state) || null}
                                        placeholder="UF"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </FormLayout>
            }
        />
    )
}

export default GeneratorLocationModal
